import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../interfaces/userType.enum';
import { IUser } from '../../interfaces/auth.interface';
import NoAccess from './NoAccess';

interface PermissionRouteProps {
    permissionKey?: string;
    anyOf?: string[];
    adminOnly?: boolean;
    platformRoles?: UserType[];
    alsoAllowIf?: (user: IUser | null) => boolean;
    /** Render a "no access" page instead of silently redirecting home. */
    showNoAccess?: boolean;
}

const PermissionRoute: React.FC<PermissionRouteProps> = ({ permissionKey, anyOf, adminOnly, platformRoles, alsoAllowIf, showNoAccess }) => {
    const { user, hasPermission, isAdmin, isSystemAdmin, permissionsReady } = useAuth();
    const location = useLocation();

    if (!user || !user.roles) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }
    if (!permissionsReady) {
        return null; // decide once the effective set has loaded — no redirect flash
    }
    // The JWT `roles` claim is a comma-joined string, never split by the rest of the client.
    const jwtRoles = (Array.isArray(user.roles) ? user.roles : String(user.roles ?? '').split(',')).map(r => r.trim());
    const platformOk = !!platformRoles && platformRoles.some(r => jwtRoles.includes(r));
    const keyOk = !!permissionKey && hasPermission(permissionKey);
    const anyOk = !!anyOf && anyOf.some(k => hasPermission(k));
    const adminOk = !!adminOnly && (isAdmin || isSystemAdmin);
    const alsoOk = alsoAllowIf ? alsoAllowIf(user) : false;
    const nothingRequested = !permissionKey && !anyOf && !adminOnly && !platformRoles;

    const allowed = nothingRequested || platformOk || keyOk || anyOk || adminOk || alsoOk;
    if (allowed) return <Outlet />;
    return showNoAccess ? <NoAccess /> : <Navigate to="/" replace />;
};

export default PermissionRoute;
